/**
 * version-probe — Layer 4 of the PWA absolute version update.
 *
 * Polls /__app-version.json on visibility change and every 15 minutes while
 * the document is visible. If the deployed commit differs from the baked
 * VITE_BUILD_ID, sets an external flag that AutoUpdater can OR-in
 * alongside the SW-based `needRefresh` flag.
 *
 * Uses `useSyncExternalStore` for zero-dependency React integration.
 * No new runtime dependencies are introduced.
 *
 * Architecture note:
 *   - Probe failures (network errors, JSON parse errors) are console.warn only.
 *     No user-visible error — the probe is a passive background listener, not a
 *     user-action handler. Per Rule 8, the obligation to surface failure applies
 *     to user-action handlers; this module is read-only / observational.
 *   - The apply path in AutoUpdater / apply-update.ts is the consequential
 *     side and must not silently swallow failures (handled there separately —
 *     every flush failure is console.error-logged).
 *   - IndexedDB and localStorage are never touched here. Only the HTTP layer.
 */

import { useSyncExternalStore } from "react";
import { checkForUpdate } from "./update-controller";

// ─── External store ───────────────────────────────────────────────────────────

// Subscribers to the external needRefresh store.
type Listener = () => void;
const listeners = new Set<Listener>();
let externalNeedRefresh = false;

function setExternalNeedRefresh(value: boolean): void {
  if (externalNeedRefresh === value) return;
  externalNeedRefresh = value;
  listeners.forEach((l) => l());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return externalNeedRefresh;
}

// SSR-safe server snapshot — always false (SPA-only, but defensive).
function getServerSnapshot(): boolean {
  return false;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * React hook — returns true when the version probe has detected that the
 * deployed commit differs from the running bundle's baked VITE_BUILD_ID.
 *
 * Combine with useRegisterSW's `needRefresh` flag via logical OR so the
 * update is applied regardless of which detection path fires first.
 */
export function useExternalNeedRefresh(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Reset the external needRefresh flag to false.
 * Retained for tests and any future caller that needs to clear the flag;
 * AutoUpdater has no dismiss path (it applies instead of prompting).
 */
export function clearExternalNeedRefresh(): void {
  setExternalNeedRefresh(false);
}

/**
 * Read the current external needRefresh flag without a React hook.
 *
 * @internal — test-only export. Do not call from application code; use
 *   `useExternalNeedRefresh()` in React components instead.
 */
export function getExternalNeedRefreshForTesting(): boolean {
  return externalNeedRefresh;
}

/**
 * Imperatively set the external needRefresh flag to true and notify React.
 *
 * Used by stale-chunk-rescue.ts when a second chunk-load failure within the
 * guard window is detected (loop protection — no immediate reload is
 * attempted; AutoUpdater schedules a clean apply instead).
 */
export function flagExternalNeedRefresh(): void {
  setExternalNeedRefresh(true);
}

// ─── Probe implementation ─────────────────────────────────────────────────────

const PROBE_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
let probeTimer: ReturnType<typeof setInterval> | null = null;
let started = false;

/**
 * Run one probe cycle: fetch /__app-version.json, compare commits, set flag.
 *
 * VITE_BUILD_ID format: "{version}-{shortSha}-{builtAtMs}"
 * We extract the middle segment (index 1 when split on "-") as the commit.
 *
 * If the version file's `commit` differs from our baked commit, the external
 * needRefresh flag is set to true and AutoUpdater applies the update.
 *
 * Also triggers navigator.serviceWorker.getRegistration() → r.update() as a
 * best-effort SW update check. Failures are swallowed — SW update() failing
 * is not user-actionable and should not surface as a visible error.
 *
 * @internal exported for tests — call via `startVersionProbe()` in the app.
 */
export async function runProbe(): Promise<void> {
  // Don't probe on hidden documents — saves battery on background tabs / PWA
  // backgrounded on iOS. Will fire again on next visibilitychange → visible.
  if (typeof document !== "undefined" && document.hidden) return;

  // Best-effort SW update ping — makes the browser re-fetch sw.js to check for
  // a new version even if the normal update check interval hasn't elapsed.
  // Delegated to update-controller.checkForUpdate() so the throttle + the
  // navigator.serviceWorker.ready gate (iOS-critical — an un-gated update() is
  // a no-op before the active worker settles) live in exactly one place. Its
  // own failure handling is console.warn only.
  void checkForUpdate();

  // Fetch the version file — cache: 'no-store' bypasses all HTTP caches.
  let json: { version?: string; commit?: string; builtAt?: string } = {};
  try {
    const res = await fetch("/__app-version.json", { cache: "no-store" });
    if (!res.ok) {
      console.warn(`[proof version-probe] /__app-version.json returned ${res.status}`);
      return;
    }
    json = (await res.json()) as typeof json;
  } catch (err) {
    console.warn("[proof version-probe] fetch failed:", err);
    return;
  }

  const deployedCommit = json.commit ?? "";
  if (!deployedCommit) return;

  // Extract our baked commit from VITE_BUILD_ID: "{version}-{commit}-{ms}".
  // The commit SHA itself may contain digits and lowercase letters only
  // (git short SHA is 7 chars by default), so splitting on "-" and taking
  // index 1 is safe for this fixed format.
  const buildId: string = import.meta.env.VITE_BUILD_ID;
  const parts = buildId.split("-");
  // Guard: if the build ID format is unexpected, fall back to full string compare.
  const runningCommit = parts.length >= 3 ? (parts[1] ?? buildId) : buildId;

  if (deployedCommit !== runningCommit) {
    setExternalNeedRefresh(true);
  }
}

/**
 * Start the version probe.
 *
 * Call once from entry.client.tsx after initTheme(). Idempotent — calling
 * more than once is a no-op after the first call.
 *
 * Guards:
 *   - `typeof navigator !== "undefined"` — SSR safety.
 *   - Runs initial probe immediately (unless document is hidden).
 *   - Re-probes on `visibilitychange → visible`.
 *   - Polls every 15 minutes while visible.
 */
export function startVersionProbe(): void {
  if (typeof navigator === "undefined") return;
  if (started) return;
  started = true;

  // Probe immediately on startup.
  void runProbe();

  // Re-probe whenever the user returns to the tab / PWA.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      void runProbe();
    }
  });

  // Poll every 15 minutes — catches long-running sessions without a tab switch.
  probeTimer = setInterval(() => {
    void runProbe();
  }, PROBE_INTERVAL_MS);

  // Silence the unused-variable warning in test environments where the timer
  // is never cleared. In a real app the probe lives forever.
  void probeTimer;
}
